import { Decimal, SCALE } from "./decimal.js";
/** PostgreSQL `bigint` sınırı: tutarlar bu aralığın dışına çıkamaz. */
const MAX_MINOR = 9223372036854775807n;
const MIN_MINOR = -9223372036854775808n;
export const CURRENCY = "TRY";
const MINOR_PER_MAJOR = 100n;
function assertInRange(minor) {
    if (minor > MAX_MINOR || minor < MIN_MINOR) {
        throw new RangeError(`Money: tutar bigint sınırlarını aşıyor (${minor})`);
    }
    return minor;
}
/**
 * Kalan dağıtımı (largest remainder): kesin değerleri tam sayıya indirirken TOPLAMI KORUR.
 * Her değer aşağı yuvarlanır; eksik kalan birimler en büyük kesir kalanına sahip olanlara, eşitlikte
 * küçük indekse verilir → aynı girdi her zaman aynı çıktıyı üretir (deterministik).
 */
export function roundPreservingSum(exact, targetTotal) {
    const floors = exact.map((value) => value.floor(0));
    const result = floors.map((value) => value.toBigInt());
    let remaining = targetTotal - result.reduce((sum, value) => sum + value, 0n);
    if (remaining < 0n || remaining > BigInt(exact.length)) {
        throw new RangeError(`roundPreservingSum: hedef toplam (${targetTotal}) kesin değerlerin toplamıyla tutarsız (fark ${remaining})`);
    }
    const order = exact
        .map((value, index) => ({ index, remainder: value.sub(floors[index]) }))
        .sort((a, b) => b.remainder.cmp(a.remainder) || a.index - b.index);
    for (const entry of order) {
        if (remaining === 0n)
            break;
        result[entry.index] += 1n;
        remaining -= 1n;
    }
    return result;
}
/**
 * Değişmez para tutarı: TRY, kuruş cinsinden `bigint` (AGENTS.md §3.2).
 * Kayan noktalı sayı hiçbir yerde kullanılmaz.
 */
export class Money {
    minorUnits;
    constructor(minorUnits) {
        this.minorUnits = minorUnits;
    }
    static ZERO = new Money(0n);
    /** Kuruş cinsinden. `number` yalnızca güvenli tam sayıysa kabul edilir. */
    static fromMinor(minor) {
        if (typeof minor === "bigint")
            return new Money(assertInRange(minor));
        if (typeof minor === "number") {
            if (!Number.isSafeInteger(minor))
                throw new RangeError(`Money.fromMinor: güvenli tam sayı değil (${minor})`);
            return new Money(BigInt(minor));
        }
        if (!/^-?\d+$/.test(minor.trim()))
            throw new RangeError(`Money.fromMinor: geçersiz kuruş değeri "${minor}"`);
        return new Money(assertInRange(BigInt(minor.trim())));
    }
    /** Lira cinsinden ondalık: "1234.56". İkiden fazla ondalık basamak HATA verir (sessiz yuvarlama yok). */
    static fromMajor(major) {
        const minor = Decimal.from(major).mul(100);
        if (!minor.isInteger()) {
            throw new RangeError(`Money.fromMajor: kuruştan küçük hassasiyet (${Decimal.from(major).toString()})`);
        }
        return new Money(assertInRange(minor.toBigInt()));
    }
    /** Ondalık tutarı kuruşa ROUND_HALF_UP ile yuvarlar. Hesap sonuçları için. */
    static fromDecimalMajor(major) {
        return new Money(assertInRange(Decimal.from(major).mul(100).round(SCALE.MONEY_MINOR).toBigInt()));
    }
    /** Türkçe biçimli girdi: "1.234,56" veya "1.234,56 ₺" */
    static parseTr(input) {
        return Money.fromMajor(Decimal.parseTr(input.replace(/₺|TL|TRY/gi, "")));
    }
    /** Tutar = birim fiyat × miktar, kuruşa yuvarlanır. */
    static fromUnitPrice(unitPrice, quantity) {
        return Money.fromDecimalMajor(Decimal.from(unitPrice).mul(quantity));
    }
    static sum(values) {
        let total = 0n;
        for (const value of values)
            total += value.minorUnits;
        return new Money(assertInRange(total));
    }
    static min(first, ...rest) {
        return rest.reduce((acc, value) => (value.lt(acc) ? value : acc), first);
    }
    static max(first, ...rest) {
        return rest.reduce((acc, value) => (value.gt(acc) ? value : acc), first);
    }
    get minor() {
        return this.minorUnits;
    }
    get currency() {
        return CURRENCY;
    }
    add(other) {
        return new Money(assertInRange(this.minorUnits + other.minorUnits));
    }
    sub(other) {
        return new Money(assertInRange(this.minorUnits - other.minorUnits));
    }
    neg() {
        return new Money(assertInRange(-this.minorUnits));
    }
    abs() {
        return this.minorUnits < 0n ? this.neg() : this;
    }
    /** Ondalık çarpanla çarpar ve kuruşa yuvarlar (ROUND_HALF_UP). */
    mul(factor) {
        return new Money(assertInRange(Decimal.from(this.minorUnits).mul(factor).round(SCALE.MONEY_MINOR).toBigInt()));
    }
    /** Yüzde uygular: `percentage("20")` → tutarın %20'si, kuruşa yuvarlanmış. */
    percentage(ratePercent) {
        return this.mul(Decimal.from(ratePercent).div(100));
    }
    /**
     * Tutarı verilen oranlara böler; KURUŞ KAYBI OLMAZ (parçaların toplamı her zaman bu tutara eşittir).
     * Oranlar negatif olamaz ve toplamları sıfırdan büyük olmalıdır.
     */
    allocate(ratios) {
        if (ratios.length === 0)
            throw new RangeError("Money.allocate: en az bir oran gerekli");
        const weights = ratios.map((ratio) => Decimal.from(ratio));
        if (weights.some((weight) => weight.isNegative()))
            throw new RangeError("Money.allocate: negatif oran");
        const totalWeight = Decimal.sum(weights);
        if (!totalWeight.isPositive())
            throw new RangeError("Money.allocate: oranların toplamı sıfırdan büyük olmalı");
        // Negatif tutar mutlak değer üzerinden dağıtılır; böylece dağıtım işaretten bağımsız ve simetrik olur.
        const sign = this.minorUnits < 0n ? -1n : 1n;
        const absolute = this.minorUnits * sign;
        const exact = weights.map((weight) => Decimal.from(absolute).mul(weight).div(totalWeight));
        return roundPreservingSum(exact, absolute).map((part) => new Money(part * sign));
    }
    /** Tutarı n eşit parçaya böler; artan kuruşlar ilk parçalara verilir. */
    allocateEvenly(parts) {
        if (!Number.isSafeInteger(parts) || parts <= 0)
            throw new RangeError("Money.allocateEvenly: parça sayısı pozitif olmalı");
        return this.allocate(Array.from({ length: parts }, () => 1));
    }
    cmp(other) {
        return this.minorUnits < other.minorUnits ? -1 : this.minorUnits > other.minorUnits ? 1 : 0;
    }
    eq(other) {
        return this.minorUnits === other.minorUnits;
    }
    lt(other) {
        return this.minorUnits < other.minorUnits;
    }
    lte(other) {
        return this.minorUnits <= other.minorUnits;
    }
    gt(other) {
        return this.minorUnits > other.minorUnits;
    }
    gte(other) {
        return this.minorUnits >= other.minorUnits;
    }
    isZero() {
        return this.minorUnits === 0n;
    }
    isNegative() {
        return this.minorUnits < 0n;
    }
    isPositive() {
        return this.minorUnits > 0n;
    }
    /** Lira cinsinden ondalık değer. */
    toDecimal() {
        return Decimal.from(this.minorUnits).div(100);
    }
    /** "1234.56" — makine tarafından okunur lira gösterimi. */
    toMajorString() {
        const absolute = this.minorUnits < 0n ? -this.minorUnits : this.minorUnits;
        const major = absolute / MINOR_PER_MAJOR;
        const minor = (absolute % MINOR_PER_MAJOR).toString().padStart(2, "0");
        return `${this.minorUnits < 0n ? "-" : ""}${major}.${minor}`;
    }
    /** Türkiye biçimi: "1.234,56 ₺" (AGENTS.md §6). */
    formatTr(options = {}) {
        const [major = "0", minor = "00"] = this.abs().toMajorString().split(".");
        const grouped = major.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        const sign = this.minorUnits < 0n ? "-" : "";
        return `${sign}${grouped},${minor}${options.symbol === false ? "" : " ₺"}`;
    }
    toString() {
        return this.minorUnits.toString();
    }
    /** JSON'da kuruş cinsinden DİZGE olarak taşınır: "123456" (bigint JSON'a, float paraya uygun değildir). */
    toJSON() {
        return this.minorUnits.toString();
    }
}
//# sourceMappingURL=money.js.map